// Scroll-triggered animation utility using IntersectionObserver.
// Add `data-animate` to any element to opt in. Optional attributes:
//   data-animate-class   — class to add when visible (default: "is-visible")
//   data-animate-threshold — visibility ratio 0–1 (default: 0.05)
//   data-animate-delay    — delay in ms before adding class (default: 0)
//   data-animate-once     — if "false", re-animates on every enter (default: true / one-shot)
//   data-animate-stagger  — delay in ms multiplied by sibling index (for child stagger)

function initScrollAnimations(root = document) {
  const elements = root.querySelectorAll('[data-animate]:not([data-animate-init])')
  if (!elements.length) return

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

  // Skip observer entirely — just reveal everything immediately
  if (prefersReducedMotion) {
    elements.forEach((el) => {
      el.classList.add(el.dataset.animateClass || 'is-visible')
      el.setAttribute('data-animate-init', '')
    })
    return
  }

  // Group elements by threshold so we create fewer observers
  const thresholdGroups = new Map()

  elements.forEach((el) => {
    el.setAttribute('data-animate-init', '')
    const threshold = parseFloat(el.dataset.animateThreshold) || 0.05
    if (!thresholdGroups.has(threshold)) thresholdGroups.set(threshold, [])
    thresholdGroups.get(threshold).push(el)
  })

  thresholdGroups.forEach((els, threshold) => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            // First observation while out of viewport — flag for no-delay later
            if (!entry.target._animateSeenOutside) entry.target._animateSeenOutside = true
            handleAnimateExit(entry.target)
            return
          }
          handleAnimateEnter(entry.target, observer)
        })
      },
      { threshold },
    )

    els.forEach((el) => observer.observe(el))
  })
}

function handleAnimateEnter(el, observer) {
  const animClass = el.dataset.animateClass || 'is-visible'
  // Only use delay for elements visible on initial load (not scrolled into view)
  const delay = el._animateSeenOutside ? 0 : parseInt(el.dataset.animateDelay, 10) || 0
  const once = el.dataset.animateOnce !== 'false'

  function apply() {
    // Defer to next frame so the browser paints the initial (hidden) state
    // first — without this, elements already in the viewport on load would
    // get both states in one frame and skip the transition.
    requestAnimationFrame(() => {
      el.classList.add(animClass)
      if (once) observer.unobserve(el)
    })
  }

  if (delay > 0) {
    el._animateTimer = setTimeout(apply, delay)
  } else {
    apply()
  }
}

function handleAnimateExit(el) {
  const once = el.dataset.animateOnce !== 'false'
  if (once) return

  clearTimeout(el._animateTimer)
  el.classList.remove(el.dataset.animateClass || 'is-visible')
}

// Auto-stagger children: add `data-animate-stagger="80"` to a parent
// and `data-animate` to each child. Each child gets an incremental delay.
function initStaggerGroups(root = document) {
  root.querySelectorAll('[data-animate-stagger]').forEach((parent) => {
    const base = parseInt(parent.dataset.animateStagger, 10) || 80
    const offset = parseInt(parent.dataset.animateStaggerOffset, 10) || 0
    parent.querySelectorAll('[data-animate]').forEach((child, i) => {
      if (!child.dataset.animateDelay) child.dataset.animateDelay = String(offset + base * i)
    })
  })
}

// Run on DOMContentLoaded; also expose for dynamic content (e.g. AJAX sections)
function setupScrollAnimations(root = document) {
  initStaggerGroups(root)
  initScrollAnimations(root)
}

document.addEventListener('DOMContentLoaded', () => setupScrollAnimations())

window.setupScrollAnimations = setupScrollAnimations

// Flip-counter: ticks a number through each integer from old → new.
// Usage: animateNumberFlip(el, 42, { suffix: '% OFF' })
// Cancels any in-flight animation on the same element.
function animateNumberFlip(el, to, options = {}) {
  const { prefix = '', suffix = '', duration = 750 } = options

  if (el._flipTimer) clearInterval(el._flipTimer)

  const from = parseInt(el.dataset.flipValue, 10)
  el.dataset.flipValue = to
  if (isNaN(from) || from === to) {
    el.textContent = prefix + to + suffix
    return
  }

  const steps = Math.abs(to - from)
  const step = from < to ? 1 : -1
  const interval = Math.max(16, Math.floor(duration / steps))
  let current = from

  el._flipTimer = setInterval(() => {
    current += step
    el.textContent = prefix + current + suffix
    if (current === to) clearInterval(el._flipTimer)
  }, interval)
}

window.animateNumberFlip = animateNumberFlip

// Fade-only reveal for dynamically replaced elements (no slide).
// Removes the animation class entirely and handles a simple opacity fade inline.
function fadeInAnimatedElements(root = document) {
  const elements = root.querySelectorAll('[data-animate]:not([data-animate-init])')
  if (!elements.length) return

  elements.forEach((el) => {
    el.classList.remove('anim-slide-up')
    el.setAttribute('data-animate-init', '')
    el.style.opacity = '0'
    el.style.transform = 'none'
    el.style.transition = 'opacity 0 ease'
    requestAnimationFrame(() => {
      el.style.opacity = '1'
    })
  })
}
